/**
 * Specification §3.1, *Section rail* (DECIDED 20261023).
 *
 * The rail lists the page's four sections under their own headings — no new
 * labels and no numbers — highlights the one on screen, and ticks a section
 * once every field it requires is valid. This module is the part with no
 * browser in it: which sections there are, what each one requires, and which
 * one counts as *on screen* given what the observer saw.
 */

import type { MissingItem } from './missing.ts'
import type { PageState } from './state.ts'

export interface RailSection {
  id: 'sec1' | 'sec2' | 'sec3' | 'sec4'
  /** The section's own heading, word for word. */
  heading: { hu: string; en: string }
}

/**
 * The four sections, in page order, under the headings the page prints.
 * `#sec4`'s heading shows the event name once there is one; its own heading,
 * which the rail keeps, is *Összesítés*.
 */
export const RAIL_SECTIONS: readonly RailSection[] = [
  { id: 'sec1', heading: { hu: 'Az esemény', en: 'The event' } },
  { id: 'sec2', heading: { hu: 'Menü', en: 'Menu' } },
  { id: 'sec3', heading: { hu: 'Kapcsolat és számlázás', en: 'Contact and billing' } },
  { id: 'sec4', heading: { hu: 'Összesítés', en: 'Summary' } },
]

/**
 * The tick's accessible name. The run prompt asks for one in both languages
 * and gives no wording; this is the default it runs on (assumption register
 * A31 `RAIL_TICK_LABEL`).
 */
export const RAIL_TICK_LABEL = { hu: 'kitöltve', en: 'complete' } as const

export interface TickInput {
  state: PageState
  /** The missing-data notice's items (`missing.ts`). */
  missing: MissingItem[]
  /** A headcount is in and the offer is priced. */
  ready: boolean
  /** The date is one the calendar can read. */
  dateOk: boolean
  /** Delivery has a slot for this start time. */
  slotOk: boolean
  /** The server says delivery is offered to this postcode (§3.7). */
  deliverable: boolean
  /** The short-notice enquiry route, which needs fewer answers. */
  enquiry: boolean
}

/**
 * Which sections are complete. A tick goes as soon as a field it rests on
 * becomes invalid again, because this is worked out afresh on every render.
 *
 * - **The event**: a name, a headcount, a date, and — on delivery — a
 *   four-digit postcode that is delivered to and a slot.
 * - **Menu**: there is an offer, with at least one dish on it.
 * - **Contact and billing**: nothing in the missing-data notice but the event
 *   name, which is the event's.
 * - **Summary**: the terms are accepted (and, on an order, the cancellation
 *   terms too).
 */
export function sectionTicks(input: TickInput): Record<RailSection['id'], boolean> {
  const { state, missing } = input
  const headcount = state.guestsField.trim() !== '' && Number(state.guestsField) >= 1
  const delivery = state.fulfilment === 'delivery'
  const postcode = /^\d{4}$/.test(state.postcode.trim())
  const event =
    state.eventName.trim() !== '' &&
    headcount &&
    input.dateOk &&
    (!delivery || (postcode && input.deliverable && input.slotOk))
  const dishes = (state.selection?.sav.length ?? 0) + (state.selection?.sw.length ?? 0)
  const contact = missing.every((item) => item.parts.every((part) => part.field === 'evname'))
  const summary = state.acceptTerms && (input.enquiry || state.acceptCancellation)
  return {
    sec1: event,
    sec2: input.ready && dishes > 0,
    sec3: contact,
    sec4: summary,
  }
}

/**
 * The section *on screen*: of those the observer reports as intersecting the
 * band below the header, the first in page order; where none is, the last one
 * whose top has passed above the band. Null before anything is known.
 */
export function activeSection(
  visible: ReadonlySet<string>,
  passed: ReadonlySet<string>,
): RailSection['id'] | null {
  const first = RAIL_SECTIONS.find((section) => visible.has(section.id))
  if (first !== undefined) return first.id
  const above = [...RAIL_SECTIONS].reverse().find((section) => passed.has(section.id))
  return above?.id ?? null
}
